import type {MutableRefObject} from 'react';
import {useCallback, useEffect, useMemo, useRef} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {useBenchlingMutation} from 'graphql-client/BenchlingMutation';
import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import useRecentActivityPolling from 'nav/sidepanel/home/useRecentActivityPolling';
import * as logging from 'util/Logging';

import {InAppNotification, InAppNotificationStatus, NotificationTab} from './InAppNotificationConstants';
import {
  IN_APP_NOTIFICATION_LISTING_QUERY,
  IN_APP_NOTIFICATION_UNREAD_SUMMARY_QUERY,
  InAppNotificationListingQueryData,
  InAppNotificationListingQueryVariables,
  InAppNotificationUnreadSummaryQueryData,
  UPDATE_IN_APP_NOTIFICATIONS_MUTATION,
  UpdateInAppNotificationsMutationData,
  UpdateInAppNotificationsMutationVariables,
} from './InAppNotificationQueries';

const LISTING_PAGE_SIZE = 50;

// Matches the mutation's per-request cap (see the resolver's MAX_UPDATES_PER_REQUEST); a "mark all
// read" spanning more loaded rows than this is split into that many updates per request.
const MAX_UPDATES_PER_REQUEST = 100;

// The statuses each tab both queries from the server and displays. Each tab has its own listing query
// for this set, so unread rows are never crowded out of the Unread tab by a window full of reads, and
// dismissed rows are never fetched. The client-side filter re-applies the same set to the loaded rows:
// a write flips a row's status in the cache locally, but the cached list still holds that id until a
// refetch, so the filter drops reads/dismissals out of the view immediately without a round-trip.
const STATUSES_BY_TAB: Record<NotificationTab, Array<InAppNotificationStatus>> = {
  [NotificationTab.ALL]: [InAppNotificationStatus.UNREAD, InAppNotificationStatus.READ],
  [NotificationTab.UNREAD]: [InAppNotificationStatus.UNREAD],
};

// Stable reference for `useMemo`/effect deps.
const EMPTY_NOTIFICATIONS: Array<InAppNotification> = [];

interface NotificationWrites {
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  dismiss: (id: string) => Promise<void>;
}

/** Split status updates into request-sized batches so a large "mark all read" stays within the cap. */
function batchUpdates(
  updates: Array<{id: string; status: InAppNotificationStatus}>
): Array<Array<{id: string; status: InAppNotificationStatus}>> {
  const batches: Array<Array<{id: string; status: InAppNotificationStatus}>> = [];
  for (let i = 0; i < updates.length; i += MAX_UPDATES_PER_REQUEST) {
    batches.push(updates.slice(i, i + MAX_UPDATES_PER_REQUEST));
  }
  return batches;
}

/**
 * The inbox's write actions (mark read, mark all read, dismiss). Each runs the `updateInAppNotifications`
 * mutation, whose response carries the updated rows and the new unread summary. Apollo's normalized
 * cache merges both on its own, so the list and the badge converge without a refetch and without the
 * client hand-writing the cache.
 *
 * Hard failures are already surfaced to the user by `createErrorLink`, and `useBenchlingMutation`
 * rejects on them, so each action swallows the rejection to keep its returned promise non-throwing for
 * imperative callers.
 *
 * @param loadedItems The rows currently loaded on the active tab; `markAllRead` marks the unread ones
 *   among these, i.e. the pages fetched so far, never unread rows the user has not paged to.
 */
function useNotificationWrites(loadedItems: Array<InAppNotification>): NotificationWrites {
  const [updateNotifications] = useBenchlingMutation<
    UpdateInAppNotificationsMutationData,
    UpdateInAppNotificationsMutationVariables
  >(UPDATE_IN_APP_NOTIFICATIONS_MUTATION);

  // `useBenchlingMutation` returns a fresh mutate function each render; hold it in a ref so the write
  // callbacks below can stay referentially stable (their consumers memoize on them) while still
  // calling the current mutate.
  const updateNotificationsRef = useRef(updateNotifications);
  updateNotificationsRef.current = updateNotifications;

  // The tail of every write issued so far. The UI does not block actions while a write is pending, so
  // overlapping calls (two quick dismisses, a dismiss during "mark all read") are chained onto it.
  const writeQueueRef = useRef<Promise<void>>(Promise.resolve());

  // Every request, across all calls and their batches, runs one at a time in call order: each response
  // overwrites the singleton unread summary in the cache, so the last one applied must come from the
  // last write. A failed batch stops the rest of its own call before that call's promise settles, but
  // never blocks writes queued behind it.
  const runUpdates = useCallback((updates: Array<{id: string; status: InAppNotificationStatus}>) => {
    const run = writeQueueRef.current.then(async () => {
      for (const batch of batchUpdates(updates)) {
        // eslint-disable-next-line no-await-in-loop
        const succeeded = await updateNotificationsRef.current({variables: {updates: batch}}).then(
          () => true,
          // A hard failure is toasted by `createErrorLink`; swallow the rejection so the caller's
          // promise stays non-throwing and the queue keeps draining.
          () => false
        );
        if (!succeeded) {
          return;
        }
      }
    });
    writeQueueRef.current = run;
    return run;
  }, []);

  const markRead = useCallback(
    (id: string) => runUpdates([{id, status: InAppNotificationStatus.READ}]),
    [runUpdates]
  );

  const markAllRead = useCallback(() => {
    // Only affects the unread rows loaded on the active tab, i.e. all pages fetched so far.
    const unreadIds = loadedItems.filter((n) => n.status === InAppNotificationStatus.UNREAD).map((n) => n.id);
    if (unreadIds.length === 0) {
      return Promise.resolve();
    }
    return runUpdates(unreadIds.map((id) => ({id, status: InAppNotificationStatus.READ})));
  }, [loadedItems, runUpdates]);

  const dismiss = useCallback(
    (id: string) => runUpdates([{id, status: InAppNotificationStatus.DISMISSED}]),
    [runUpdates]
  );

  return {markRead, markAllRead, dismiss};
}

export interface UnreadSummary {
  unreadCount: number;
  latestEventAt: string | null;
}

export interface UseInAppNotificationsResult {
  unreadCount: number;
  notifications: Array<InAppNotification>;
  isLoading: boolean;
  isError: boolean;
  // True once the active tab has more pages to fetch (`nextToken` is non-empty).
  hasMore: boolean;
  // Fetches and appends the active tab's next page. A no-op while a page is already in flight or when
  // there are no more pages.
  loadMore: () => void;
  // True when the summary reports an event newer than the active tab's newest loaded row, i.e. a new
  // notification has arrived since the list was last (re)loaded. Drives the panel's "New
  // notifications" pill; clicking it calls `refreshHead`.
  hasNewNotifications: boolean;
  // Refreshes the active tab's listing from the head. Interim behavior is a full refetch of the tab's
  // pages; the head-prepend follow-up swaps this body to prepend new rows onto the accumulated tail.
  refreshHead: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  dismiss: (id: string) => Promise<void>;
  refetch: () => Promise<void>;
}

interface PagedNotificationListing {
  items: Array<InAppNotification>;
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  loadMore: () => void;
  refreshHead: () => Promise<void>;
  // Newest loaded event timestamp for this tab (listing is newest-first), or null when nothing is
  // loaded. A ref so callers can compare against the summary without re-running on every array change.
  newestLoadedEventAtRef: MutableRefObject<string | null>;
}

/**
 * One tab's listing subscription: a cursor-paged `inAppNotificationListing` query for a fixed status
 * set, lazily activated (skipped until first opened, then kept subscribed cache-first) and paged by
 * appending `nextToken` pages to the loaded list. Called once per tab so each tab owns its own token
 * and accumulated pages, which is what makes per-tab pagination and scroll-depth retention natural.
 *
 * `refreshHead` is the forward-compat seam: its interim body is a full refetch; the head-prepend
 * follow-up rewrites only this body to prepend new arrivals while preserving the accumulated tail.
 */
function usePagedNotificationListing({
  statuses,
  skip,
}: {
  statuses: Array<InAppNotificationStatus>;
  skip: boolean;
}): PagedNotificationListing {
  // Guards `loadMore` against overlapping fetches (a fast double-scroll before the page resolves).
  const isFetchingMoreRef = useRef(false);

  const listingResult = useSkippableBenchlingQuery<
    InAppNotificationListingQueryData,
    InAppNotificationListingQueryVariables
  >(IN_APP_NOTIFICATION_LISTING_QUERY, {
    skip,
    variables: {pageSize: LISTING_PAGE_SIZE, statuses},
    fetchPolicy: 'cache-first',
  });

  const isLoaded = listingResult.type === QueryResultType.SUCCESS;
  // Retain the last loaded page while a `fetchMore` is in flight to keep scroll position steady.
  const lastLoadedDataRef = useRef<InAppNotificationListingQueryData | null>(null);
  if (isLoaded) {
    lastLoadedDataRef.current = listingResult.data;
  }
  const loadedData = isLoaded ? listingResult.data : lastLoadedDataRef.current;

  const items = loadedData?.inAppNotificationListing.items ?? EMPTY_NOTIFICATIONS;
  // The server returns an empty cursor once there are no more pages.
  const nextToken = loadedData?.inAppNotificationListing.nextToken ?? '';
  const hasMore = nextToken !== '';

  // Only updated while loaded, so a transient ERROR keeps the last known timestamp instead of
  // resetting to null.
  const newestLoadedEventAtRef = useRef<string | null>(null);
  if (isLoaded) {
    newestLoadedEventAtRef.current = items.length > 0 ? items[0].lastEventAt : null;
  }

  const {fetchMore, refetch} = listingResult;

  const loadMore = useCallback(() => {
    if (nextToken === '' || isFetchingMoreRef.current) {
      return;
    }
    isFetchingMoreRef.current = true;
    // `nextToken` is an opaque cursor: pass it back verbatim and never parse or reconstruct it, so a
    // future backend token-shape change stays invisible to this code.
    fetchMore({
      variables: {pageSize: LISTING_PAGE_SIZE, nextToken, statuses},
      // Append the next page's items to the ones already loaded and adopt its cursor, rather than
      // replacing the accumulated list.
      updateQuery: (previous, {fetchMoreResult}) => {
        if (fetchMoreResult == null) {
          return previous;
        }
        return {
          ...fetchMoreResult,
          inAppNotificationListing: {
            ...fetchMoreResult.inAppNotificationListing,
            items: [
              ...previous.inAppNotificationListing.items,
              ...fetchMoreResult.inAppNotificationListing.items,
            ],
          },
        };
      },
    })
      .catch((e: unknown) => {
        // A failed page also surfaces through the query result's error state on the next render, so this
        // stays silent for the user; log to Sentry so the failure is still observable.
        logging.error(e instanceof Error ? e : new Error('Failed to load more in-app notifications'));
      })
      .finally(() => {
        isFetchingMoreRef.current = false;
      });
  }, [fetchMore, nextToken, statuses]);

  const refreshHead = useCallback(async () => {
    // Interim behavior: refetch this tab's pages from the head. The head-prepend follow-up rewrites
    // only this body to prepend new arrivals onto the accumulated tail.
    await refetch();
  }, [refetch]);

  return {
    items,
    // Only the initial load (nothing retained yet) shows the panel's loading state. Once a page has
    // loaded, the transient PENDING of a `fetchMore`/refetch keeps rendering the retained rows above
    // rather than flashing the full-panel throbber, which would unmount the list and reset its scroll.
    isLoading: listingResult.type === QueryResultType.PENDING && lastLoadedDataRef.current == null,
    isError: listingResult.type === QueryResultType.ERROR,
    hasMore,
    loadMore,
    refreshHead,
    newestLoadedEventAtRef,
  };
}

/**
 * Data layer for the notification inbox. Polls the lightweight unread summary for the badge and backs
 * each tab with its own cursor-paged listing query, lazily activated the first time that tab is
 * opened. When the summary reports an event newer than the active tab's newest loaded row it surfaces
 * `hasNewNotifications` rather than refetching, so accumulated pages and scroll depth are preserved
 * and the refresh happens only when the user clicks the panel's "New notifications" pill
 * (`refreshHead`). The pill only shows on the tab the user is actively looking at; two cases reconcile
 * the head silently instead: an arrival while the open tab is empty (no pill to float over an empty
 * list and no accumulated context to preserve), and an arrival waiting on a tab the user is not
 * currently looking at (the panel is closed, or another tab is active) — reopening or switching back
 * pulls it in from the head rather than flashing the pill for a list the user has not seen since the
 * arrival. Writes run the `updateInAppNotifications` mutation, whose response lets Apollo merge each
 * row's new status into the listing and the new unread count into the badge without refetching either.
 * `refetch` is for explicit consumer-driven refreshes and is not called automatically after a write.
 *
 * @param activeTab Which tab's notifications to load.
 * @param isPanelOpen When false, skips the heavier listing queries so the closed panel only pays for
 *   the summary poll until it is first opened.
 */
export default function useInAppNotifications(
  activeTab: NotificationTab,
  {isPanelOpen}: {isPanelOpen: boolean}
): UseInAppNotificationsResult {
  const summaryResult = useBenchlingQuery<InAppNotificationUnreadSummaryQueryData>(
    IN_APP_NOTIFICATION_UNREAD_SUMMARY_QUERY,
    {fetchPolicy: 'cache-and-network'}
  );

  // The badge should always stay current, so poll unconditionally (hasInProgress: true) and let the
  // shared hook pause polling whenever the tab is backgrounded.
  // How often to refresh the unread-count badge is config-driven so it can be tuned per tenant without a deploy.
  useRecentActivityPolling({
    hasInProgress: true,
    pollIntervalMs: BenchlingConfig.IN_APP_NOTIFICATION_UNREAD_SUMMARY_POLL_INTERVAL_MS,
    startPolling: summaryResult.startPolling,
    stopPolling: summaryResult.stopPolling,
    refetch: summaryResult.refetch,
  });

  // Activate each tab's listing the first time that tab is opened; afterwards keep it subscribed with
  // a cache-first policy so revisiting the tab reads from the cache instead of hitting the network
  // again. A tab stays skipped until it has been the active tab while the panel is open at least once.
  const hasOpenedTabRef = useRef<Record<NotificationTab, boolean>>({
    [NotificationTab.ALL]: false,
    [NotificationTab.UNREAD]: false,
  });
  if (isPanelOpen) {
    hasOpenedTabRef.current[activeTab] = true;
  }

  const allListing = usePagedNotificationListing({
    statuses: STATUSES_BY_TAB[NotificationTab.ALL],
    skip: !hasOpenedTabRef.current[NotificationTab.ALL],
  });
  const unreadListing = usePagedNotificationListing({
    statuses: STATUSES_BY_TAB[NotificationTab.UNREAD],
    skip: !hasOpenedTabRef.current[NotificationTab.UNREAD],
  });
  const activeListing = activeTab === NotificationTab.UNREAD ? unreadListing : allListing;

  // Keep the last successful summary so a transient poll failure (which flips the query result to
  // ERROR even while Apollo still holds the prior data) does not blank the badge to 0 or reset the
  // latest-event timestamp. On error we keep rendering the last known values until the next good
  // poll reconciles them.
  const lastSummaryRef = useRef<UnreadSummary>({unreadCount: 0, latestEventAt: null});
  if (summaryResult.type === QueryResultType.SUCCESS) {
    const summary = summaryResult.data.inAppNotificationUnreadSummary;
    lastSummaryRef.current = {unreadCount: summary.unreadCount, latestEventAt: summary.latestEventAt};
  }
  const {unreadCount} = lastSummaryRef.current;

  // A failing badge poll stays silent for the user (the last-good values above keep rendering), so log
  // to Sentry to keep the failure observable.
  const summaryResultType = summaryResult.type;
  const summaryError = summaryResult.type === QueryResultType.ERROR ? summaryResult.error : null;
  const wasSummaryErroringRef = useRef(false);
  useEffect(() => {
    if (summaryResultType === QueryResultType.ERROR) {
      if (!wasSummaryErroringRef.current) {
        wasSummaryErroringRef.current = true;
        logging.error(summaryError ?? new Error('In-app notification unread-summary poll failed'));
      }
    } else if (summaryResultType === QueryResultType.SUCCESS) {
      wasSummaryErroringRef.current = false;
    }
  }, [summaryResultType, summaryError]);

  // Newest event timestamp among the user's UNREAD notifications; kept current by the poll, so an
  // advance past the active tab's newest loaded row signals a newly arrived notification.
  const summaryLatestEventAt = lastSummaryRef.current.latestEventAt;

  // Filter the loaded rows to the active tab's statuses so a locally read or dismissed row (whose
  // cache status changed without a refetch) drops out of the view immediately.
  const activeItems = activeListing.items;
  const notificationItems = useMemo(
    () => activeItems.filter((n) => STATUSES_BY_TAB[activeTab].includes(n.status)),
    [activeItems, activeTab]
  );

  // A newer notification exists when the summary reports an event newer than the active tab's newest loaded row
  const newestLoadedEventAt = activeListing.newestLoadedEventAtRef.current;
  const hasNewerArrival =
    summaryLatestEventAt != null &&
    (newestLoadedEventAt == null || summaryLatestEventAt > newestLoadedEventAt);

  // The "New notifications" pill is only shown on the tab the user is actively looking at.
  // So each render flags every tab that is not the one being viewed yet.
  const deferredReconcileByTabRef = useRef<Record<NotificationTab, boolean>>({
    [NotificationTab.ALL]: false,
    [NotificationTab.UNREAD]: false,
  });
  const allNewestLoadedEventAtRef = allListing.newestLoadedEventAtRef;
  const unreadNewestLoadedEventAtRef = unreadListing.newestLoadedEventAtRef;
  useEffect(() => {
    const viewedTab = isPanelOpen ? activeTab : null;
    const loadedByTab: Record<NotificationTab, string | null> = {
      [NotificationTab.ALL]: allNewestLoadedEventAtRef.current,
      [NotificationTab.UNREAD]: unreadNewestLoadedEventAtRef.current,
    };
    (Object.keys(loadedByTab) as Array<NotificationTab>).forEach((tab) => {
      const loaded = loadedByTab[tab];
      const tabHasNewerArrival =
        summaryLatestEventAt != null && loaded != null && summaryLatestEventAt > loaded;
      if (tab !== viewedTab && tabHasNewerArrival) {
        deferredReconcileByTabRef.current[tab] = true;
      }
    });
  }, [summaryLatestEventAt, isPanelOpen, activeTab, allNewestLoadedEventAtRef, unreadNewestLoadedEventAtRef]);

  // Suppress the pill the instant a deferred reconcile is pending for the active tab, so the silent head
  // refresh below lands without the pill flashing first.
  const hasNewNotifications = hasNewerArrival && !deferredReconcileByTabRef.current[activeTab];

  const summaryRefetch = summaryResult.refetch;
  const activeRefreshHead = activeListing.refreshHead;
  const isActiveTabOpened = hasOpenedTabRef.current[activeTab];

  // Silently reconcile the active tab's head with a newer arrival in the two cases where the pill is
  // deliberately withheld: (1) the tab is showing an empty list, which has no rows to float the pill
  // over and no accumulated pages or scroll depth to preserve; and (2) the tab's head fell behind while
  // it was not the one being viewed (`deferredReconcileByTabRef`)
  const {isLoading: activeIsLoading, isError: activeIsError} = activeListing;
  const autoRefreshedEventAtByTabRef = useRef<Record<NotificationTab, string | null>>({
    [NotificationTab.ALL]: null,
    [NotificationTab.UNREAD]: null,
  });
  const autoRefreshInFlightByTabRef = useRef<Record<NotificationTab, boolean>>({
    [NotificationTab.ALL]: false,
    [NotificationTab.UNREAD]: false,
  });
  useEffect(() => {
    const tabReady = isPanelOpen && !activeIsLoading && !activeIsError;
    const shouldReconcileSilently =
      notificationItems.length === 0 || deferredReconcileByTabRef.current[activeTab];
    const alreadyHandlingArrival =
      autoRefreshInFlightByTabRef.current[activeTab] ||
      autoRefreshedEventAtByTabRef.current[activeTab] === summaryLatestEventAt;
    if (!tabReady || !hasNewerArrival || !shouldReconcileSilently || alreadyHandlingArrival) {
      return;
    }
    const refreshedTab = activeTab;
    const refreshedEventAt = summaryLatestEventAt;
    autoRefreshInFlightByTabRef.current[refreshedTab] = true;
    activeRefreshHead()
      .then(() => {
        // Mark the event handled only after the refresh lands, so a refresh that returns still-empty
        // does not re-fire for the same timestamp, and clear the deferred flag now that the head is
        // level again.
        autoRefreshedEventAtByTabRef.current[refreshedTab] = refreshedEventAt;
        deferredReconcileByTabRef.current[refreshedTab] = false;
      })
      .catch((e: unknown) => {
        // A failed reconcile leaves the prior view in place and the event un-handled; a later
        // qualifying render re-arms this refresh. It stays silent for the user (the listing's own
        // error state covers the UI), so log to Sentry to keep the failure observable.
        logging.error(e instanceof Error ? e : new Error('Failed to auto-refresh in-app notifications'));
      })
      .finally(() => {
        autoRefreshInFlightByTabRef.current[refreshedTab] = false;
      });
  }, [
    isPanelOpen,
    activeTab,
    activeIsLoading,
    activeIsError,
    notificationItems.length,
    hasNewerArrival,
    summaryLatestEventAt,
    activeRefreshHead,
  ]);

  const refetch = useCallback(async () => {
    // Only refresh the active tab's listing if it has actually been opened; refetching a still-skipped
    // query would fire a network request for a tab the user has never viewed.
    await Promise.all([summaryRefetch(), isActiveTabOpened ? activeRefreshHead() : Promise.resolve()]);
  }, [summaryRefetch, activeRefreshHead, isActiveTabOpened]);

  const {markRead, markAllRead, dismiss} = useNotificationWrites(notificationItems);

  const {isLoading, isError, hasMore, loadMore} = activeListing;

  return useMemo(
    () => ({
      unreadCount,
      notifications: notificationItems,
      isLoading,
      isError,
      hasMore,
      loadMore,
      hasNewNotifications,
      refreshHead: activeRefreshHead,
      markRead,
      markAllRead,
      dismiss,
      refetch,
    }),
    [
      unreadCount,
      notificationItems,
      isLoading,
      isError,
      hasMore,
      loadMore,
      hasNewNotifications,
      activeRefreshHead,
      markRead,
      markAllRead,
      dismiss,
      refetch,
    ]
  );
}
